/**
 * Vik (Jan 23, 2026) : ThemeEditor.js - Centralized Design System Editor
 *
 * PURPOSE:
 * This component provides a UI for managing app-wide styles including:
 * - Colors (primary, secondary, background, text, etc.)
 * - Typography presets (heading-1, heading-2, body, etc.)
 * - Spacing (section padding, card padding, gaps)
 * - Borders (radius, colors, shadows)
 *
 * HOW IT WORKS:
 * 1. Receives cfg (config), setCfg, saveConfig from UnifiedEditor
 * 2. Modifies cfg.theme object with user's style choices
 * 3. Calls saveConfig() to persist changes to Firestore
 * 4. ThemeContext.js injects these values as CSS variables
 * 5. All components use CSS variables, so changes reflect app-wide
 *
 * SCHEMA LOCATION: cfg.theme (extends existing theme object)
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  FaPalette,
  FaFont,
  FaRulerCombined,
  FaBorderStyle,
  FaSave,
  FaUndo,
  FaEye,
} from "react-icons/fa";

// Vik (Jan 23, 2026) : Default theme structure - used when no theme exists in config
const DEFAULT_THEME = {
  name: "Custom Theme",

  // Vik (Jan 23, 2026) : Color palette - these become CSS variables like --color-primary
  colors: {
    primary: "#667eea",
    secondary: "#764ba2",
    accent: "#ff6b6b",
    success: "#38ef7d",
    warning: "#f9a825",
    error: "#ef5350",
    background: "#0b1221",
    surface: "#1a2035",
    text: "#ffffff",
    textSecondary: "rgba(255,255,255,0.7)",
    border: "rgba(255,255,255,0.1)",
  },

  // Vik (Jan 23, 2026) : Typography presets - reusable text styles like Wix's text themes
  typography: {
    fontFamilies: {
      primary: "'Inter', sans-serif",
      heading: "'Poppins', sans-serif",
    },
    presets: {
      "heading-1": {
        fontSize: "2.5rem",
        fontWeight: "600",
        lineHeight: "1.3",
        letterSpacing: "0",
      },
      "heading-2": {
        fontSize: "2rem",
        fontWeight: "600",
        lineHeight: "1.35",
        letterSpacing: "0",
      },
      "heading-3": {
        fontSize: "1.5rem",
        fontWeight: "600",
        lineHeight: "1.4",
        letterSpacing: "0",
      },
      subtitle: {
        fontSize: "1.25rem",
        fontWeight: "500",
        lineHeight: "1.5",
        letterSpacing: "0.01em",
      },
      body: {
        fontSize: "1rem",
        fontWeight: "400",
        lineHeight: "1.6",
        letterSpacing: "0",
      },
      "body-small": {
        fontSize: "0.875rem",
        fontWeight: "400",
        lineHeight: "1.5",
        letterSpacing: "0",
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: "400",
        lineHeight: "1.4",
        letterSpacing: "0.02em",
      },
    },
  },

  // Vik (Jan 23, 2026) : Spacing system - consistent spacing across the app
  spacing: {
    sectionPaddingY: "80px",
    sectionPaddingX: "5%",
    cardPadding: "24px",
    gap: "16px",
    gapSmall: "8px",
    gapLarge: "32px",
  },

  // Vik (Jan 23, 2026) : Border styles - radius, width, shadows
  borders: {
    radiusSmall: "4px",
    radiusMedium: "8px",
    radiusLarge: "16px",
    radiusFull: "9999px",
    width: "1px",
    // Vik (Jan 23, 2026) : Shadow presets for elevation
    shadowSmall: "0 1px 2px rgba(0,0,0,0.1)",
    shadowMedium: "0 4px 6px rgba(0,0,0,0.15)",
    shadowLarge: "0 10px 15px rgba(0,0,0,0.2)",
  },
};

// Vik (Jan 23, 2026) : Tab definitions for the editor UI
const TABS = [
  { id: "colors", label: "Colors", icon: <FaPalette /> },
  { id: "typography", label: "Typography", icon: <FaFont /> },
  { id: "spacing", label: "Spacing", icon: <FaRulerCombined /> },
  { id: "borders", label: "Borders", icon: <FaBorderStyle /> },
];

/**
 * Vik (Jan 23, 2026) : Main ThemeEditor component
 *
 * Props:
 * - cfg: The full app config object
 * - setCfg: Function to update config in UnifiedEditor state
 * - saveConfig: Function to persist config to Firestore
 * - appId: Current app identifier
 */
export default function ThemeEditor({ cfg, setCfg, saveConfig, appId }) {
  // Vik (Jan 23, 2026) : Active tab state - which editor section is visible
  const [activeTab, setActiveTab] = useState("colors");

  // Vik (Jan 23, 2026) : Local theme state - edited before saving
  const [localTheme, setLocalTheme] = useState(DEFAULT_THEME);

  // Vik (Jan 23, 2026) : Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Vik (Jan 23, 2026) : Saving state for UI feedback
  const [saving, setSaving] = useState(false);

  // Vik (Jan 23, 2026) : Initialize local theme from config when component mounts or cfg changes
  useEffect(() => {
    if (cfg?.theme) {
      // Vik (Jan 23, 2026) : Merge config theme with defaults to ensure all fields exist
      setLocalTheme(deepMerge(DEFAULT_THEME, cfg.theme));
    }
  }, [cfg]);

  /**
   * Vik (Jan 23, 2026) : Update a specific theme value
   *
   * @param {string} path - Dot-notation path like "colors.primary" or "typography.presets.heading-1.fontSize"
   * @param {any} value - The new value to set
   */
  const updateThemeValue = useCallback((path, value) => {
    setLocalTheme((prev) => {
      const updated = { ...prev };
      setNestedValue(updated, path, value);
      return updated;
    });
    setHasChanges(true);
  }, []);

  /**
   * Vik (Jan 23, 2026) : Save theme to config and persist to Firestore
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      // Vik (Jan 23, 2026) : Create updated config with new theme
      const updatedCfg = {
        ...cfg,
        theme: localTheme,
      };

      // Vik (Jan 23, 2026) : Update parent state
      setCfg(updatedCfg);

      // Vik (Jan 23, 2026) : Persist to Firestore
      await saveConfig(updatedCfg);

      setHasChanges(false);

      // Vik (Jan 23, 2026) : Dispatch event so ThemeContext picks up changes
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", { detail: { appId } }),
      );
    } catch (error) {
      console.error("Vik (Jan 23, 2026) : Failed to save theme:", error);
      alert("Failed to save theme. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Vik (Jan 23, 2026) : Reset theme to config values (discard changes)
   */
  const handleReset = () => {
    if (cfg?.theme) {
      setLocalTheme(deepMerge(DEFAULT_THEME, cfg.theme));
    } else {
      setLocalTheme(DEFAULT_THEME);
    }
    setHasChanges(false);
  };

  return (
    <div className="editor-card mb-4">
      {/* Vik (Jan 23, 2026) : Header with title and action buttons */}
      <div
        className="card-header-flex"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h3 style={{ margin: 0, color: "#fff" }}>
          <FaPalette style={{ marginRight: "10px" }} />
          Design System
        </h3>
        <div style={{ display: "flex", gap: "10px" }}>
          {/* Vik (Jan 23, 2026) : Reset button - only show if there are changes */}
          {hasChanges && (
            <button
              onClick={handleReset}
              style={{
                padding: "8px 16px",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "6px",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaUndo /> Reset
            </button>
          )}
          {/* Vik (Jan 23, 2026) : Save button */}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{
              padding: "8px 16px",
              background: hasChanges
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "rgba(255,255,255,0.1)",
              border: "none",
              borderRadius: "6px",
              color: "#fff",
              cursor: hasChanges ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: hasChanges ? 1 : 0.5,
            }}
          >
            <FaSave /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Vik (Jan 23, 2026) : Tab navigation */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          marginBottom: "20px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          paddingBottom: "10px",
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px",
              background:
                activeTab === tab.id
                  ? "rgba(102, 126, 234, 0.2)"
                  : "transparent",
              border: "none",
              borderBottom:
                activeTab === tab.id
                  ? "2px solid #667eea"
                  : "2px solid transparent",
              color: activeTab === tab.id ? "#fff" : "rgba(255,255,255,0.6)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Vik (Jan 23, 2026) : Tab content - render appropriate editor based on active tab */}
      <div style={{ minHeight: "400px" }}>
        {activeTab === "colors" && (
          <ColorsEditor
            colors={localTheme.colors}
            onChange={(path, value) =>
              updateThemeValue(`colors.${path}`, value)
            }
          />
        )}
        {activeTab === "typography" && (
          <TypographyEditor
            typography={localTheme.typography}
            onChange={(path, value) =>
              updateThemeValue(`typography.${path}`, value)
            }
          />
        )}
        {activeTab === "spacing" && (
          <SpacingEditor
            spacing={localTheme.spacing}
            onChange={(path, value) =>
              updateThemeValue(`spacing.${path}`, value)
            }
          />
        )}
        {activeTab === "borders" && (
          <BordersEditor
            borders={localTheme.borders}
            onChange={(path, value) =>
              updateThemeValue(`borders.${path}`, value)
            }
          />
        )}
      </div>

      {/* Vik (Jan 23, 2026) : Unsaved changes indicator */}
      {hasChanges && (
        <div
          style={{
            marginTop: "20px",
            padding: "10px 16px",
            background: "rgba(255, 193, 7, 0.1)",
            border: "1px solid rgba(255, 193, 7, 0.3)",
            borderRadius: "6px",
            color: "#ffc107",
            fontSize: "0.9rem",
          }}
        >
          ⚠️ You have unsaved changes. Click "Save Changes" to apply them.
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : COLORS EDITOR
   Edit color palette values with color pickers
   ═══════════════════════════════════════════════════════════════════════════ */

function ColorsEditor({ colors, onChange }) {
  // Vik (Jan 23, 2026) : Group colors by category for better organization
  const colorGroups = {
    "Brand Colors": ["primary", "secondary", "accent"],
    "Status Colors": ["success", "warning", "error"],
    Background: ["background", "surface"],
    Text: ["text", "textSecondary"],
    "UI Elements": ["border"],
  };

  return (
    <div>
      {Object.entries(colorGroups).map(([groupName, colorKeys]) => (
        <div key={groupName} style={{ marginBottom: "24px" }}>
          <h4 style={{ color: "#fff", marginBottom: "12px", fontSize: "1rem" }}>
            {groupName}
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "12px",
            }}
          >
            {colorKeys.map((key) => (
              <ColorInput
                key={key}
                label={formatLabel(key)}
                value={colors[key] || "#000000"}
                onChange={(value) => onChange(key, value)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Vik (Jan 23, 2026) : TODO - Add custom colors section for user-defined colors */}
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "8px",
          border: "1px dashed rgba(255,255,255,0.2)",
        }}
      >
        <p
          style={{
            color: "rgba(255,255,255,0.5)",
            margin: 0,
            fontSize: "0.9rem",
          }}
        >
          💡 TODO: Add support for custom color slots that users can define and
          name themselves.
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : TYPOGRAPHY EDITOR
   Edit font families and text style presets
   ═══════════════════════════════════════════════════════════════════════════ */

function TypographyEditor({ typography, onChange }) {
  // Vik (Jan 23, 2026) : Common font options - extend this list as needed
  const fontOptions = [
    "'Inter', sans-serif",
    "'Poppins', sans-serif",
    "'Roboto', sans-serif",
    "'Open Sans', sans-serif",
    "'Montserrat', sans-serif",
    "'Lato', sans-serif",
    "'Playfair Display', serif",
    "'Merriweather', serif",
    "'Source Code Pro', monospace",
  ];

  return (
    <div>
      {/* Vik (Jan 23, 2026) : Font Family Selection */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Font Families</h4>
        <p
          style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.9rem",
            marginBottom: "16px",
          }}
        >
          Choose the fonts used throughout your app.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.875rem",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Primary Font (Body text)
            </label>
            <select
              value={typography.fontFamilies?.primary || fontOptions[0]}
              onChange={(e) => onChange("fontFamilies.primary", e.target.value)}
              style={selectStyle}
            >
              {fontOptions.map((font) => (
                <option key={font} value={font}>
                  {font.split(",")[0].replace(/'/g, "")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.875rem",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Heading Font
            </label>
            <select
              value={typography.fontFamilies?.heading || fontOptions[1]}
              onChange={(e) => onChange("fontFamilies.heading", e.target.value)}
              style={selectStyle}
            >
              {fontOptions.map((font) => (
                <option key={font} value={font}>
                  {font.split(",")[0].replace(/'/g, "")}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Vik (Jan 23, 2026) : Typography Presets */}
      <div>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Text Presets</h4>
        <p
          style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.9rem",
            marginBottom: "16px",
          }}
        >
          Define reusable text styles. Components can reference these presets
          for consistent typography.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {Object.entries(typography.presets || {}).map(
            ([presetName, preset]) => (
              <TypographyPresetEditor
                key={presetName}
                name={presetName}
                preset={preset}
                fontFamily={
                  presetName.includes("heading")
                    ? typography.fontFamilies?.heading
                    : typography.fontFamilies?.primary
                }
                onChange={(field, value) =>
                  onChange(`presets.${presetName}.${field}`, value)
                }
              />
            ),
          )}
        </div>
      </div>

      {/* Vik (Jan 23, 2026) : TODO - Add ability to create new custom presets */}
      <div
        style={{
          marginTop: "20px",
          padding: "16px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "8px",
          border: "1px dashed rgba(255,255,255,0.2)",
        }}
      >
        <p
          style={{
            color: "rgba(255,255,255,0.5)",
            margin: 0,
            fontSize: "0.9rem",
          }}
        >
          💡 TODO: Add button to create new custom typography presets.
        </p>
      </div>
    </div>
  );
}

/**
 * Vik (Jan 23, 2026) : Single typography preset editor row
 */
function TypographyPresetEditor({ name, preset, fontFamily, onChange }) {
  return (
    <div
      style={{
        padding: "16px",
        background: "rgba(255,255,255,0.05)",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {/* Vik (Jan 23, 2026) : Preset name and preview */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <span style={{ color: "#fff", fontWeight: 500 }}>
          {formatLabel(name)}
        </span>
        <span
          style={{
            fontFamily: fontFamily,
            fontSize: preset.fontSize,
            fontWeight: preset.fontWeight,
            lineHeight: preset.lineHeight,
            color: "rgba(255,255,255,0.8)",
          }}
        >
          Preview Text
        </span>
      </div>

      {/* Vik (Jan 23, 2026) : Preset controls */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
        }}
      >
        <div>
          <label style={labelStyle}>Font Size</label>
          <input
            type="text"
            value={preset.fontSize}
            onChange={(e) => onChange("fontSize", e.target.value)}
            style={inputStyle}
            placeholder="e.g., 2rem"
          />
        </div>
        <div>
          <label style={labelStyle}>Font Weight</label>
          <select
            value={preset.fontWeight}
            onChange={(e) => onChange("fontWeight", e.target.value)}
            style={selectStyle}
          >
            <option value="300">Light (300)</option>
            <option value="400">Regular (400)</option>
            <option value="500">Medium (500)</option>
            <option value="600">Semi-Bold (600)</option>
            <option value="700">Bold (700)</option>
            <option value="800">Extra-Bold (800)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Line Height</label>
          <input
            type="text"
            value={preset.lineHeight}
            onChange={(e) => onChange("lineHeight", e.target.value)}
            style={inputStyle}
            placeholder="e.g., 1.5"
          />
        </div>
        <div>
          <label style={labelStyle}>Letter Spacing</label>
          <input
            type="text"
            value={preset.letterSpacing}
            onChange={(e) => onChange("letterSpacing", e.target.value)}
            style={inputStyle}
            placeholder="e.g., 0.02em"
          />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : SPACING EDITOR
   Edit spacing values used throughout the app
   ═══════════════════════════════════════════════════════════════════════════ */

function SpacingEditor({ spacing, onChange }) {
  // Vik (Jan 23, 2026) : Spacing field definitions with descriptions
  const spacingFields = [
    {
      key: "sectionPaddingY",
      label: "Section Vertical Padding",
      description: "Top/bottom padding for page sections",
    },
    {
      key: "sectionPaddingX",
      label: "Section Horizontal Padding",
      description: "Left/right padding for page sections",
    },
    {
      key: "cardPadding",
      label: "Card Padding",
      description: "Internal padding for cards and containers",
    },
    {
      key: "gap",
      label: "Default Gap",
      description: "Standard gap between elements",
    },
    {
      key: "gapSmall",
      label: "Small Gap",
      description: "Smaller gap for compact layouts",
    },
    {
      key: "gapLarge",
      label: "Large Gap",
      description: "Larger gap for spacious layouts",
    },
  ];

  return (
    <div>
      <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: "20px" }}>
        Define consistent spacing values. Use rem, px, or % units.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "16px",
        }}
      >
        {spacingFields.map((field) => (
          <div
            key={field.key}
            style={{
              padding: "16px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "8px",
            }}
          >
            <label
              style={{
                color: "#fff",
                fontWeight: 500,
                display: "block",
                marginBottom: "4px",
              }}
            >
              {field.label}
            </label>
            <p
              style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.8rem",
                margin: "0 0 8px 0",
              }}
            >
              {field.description}
            </p>
            <input
              type="text"
              value={spacing[field.key] || ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              style={inputStyle}
              placeholder="e.g., 24px, 2rem, 5%"
            />
          </div>
        ))}
      </div>

      {/* Vik (Jan 23, 2026) : Visual spacing preview */}
      <div style={{ marginTop: "24px" }}>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Preview</h4>
        <div
          style={{
            padding: spacing.sectionPaddingY || "80px",
            paddingLeft: spacing.sectionPaddingX || "5%",
            paddingRight: spacing.sectionPaddingX || "5%",
            background: "rgba(102, 126, 234, 0.1)",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              padding: spacing.cardPadding || "24px",
              background: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              display: "flex",
              gap: spacing.gap || "16px",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "rgba(102, 126, 234, 0.5)",
                borderRadius: "8px",
              }}
            />
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "rgba(102, 126, 234, 0.5)",
                borderRadius: "8px",
              }}
            />
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "rgba(102, 126, 234, 0.5)",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : BORDERS EDITOR
   Edit border radius, width, and shadow presets
   ═══════════════════════════════════════════════════════════════════════════ */

function BordersEditor({ borders, onChange }) {
  return (
    <div>
      {/* Vik (Jan 23, 2026) : Border Radius */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Border Radius</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
          }}
        >
          {["radiusSmall", "radiusMedium", "radiusLarge", "radiusFull"].map(
            (key) => (
              <div key={key}>
                <label style={labelStyle}>
                  {formatLabel(key.replace("radius", ""))}
                </label>
                <input
                  type="text"
                  value={borders[key] || ""}
                  onChange={(e) => onChange(key, e.target.value)}
                  style={inputStyle}
                  placeholder="e.g., 8px"
                />
                {/* Vik (Jan 23, 2026) : Visual preview of radius */}
                <div
                  style={{
                    marginTop: "8px",
                    width: "40px",
                    height: "40px",
                    background: "rgba(102, 126, 234, 0.5)",
                    borderRadius: borders[key] || "0",
                  }}
                />
              </div>
            ),
          )}
        </div>
      </div>

      {/* Vik (Jan 23, 2026) : Border Width */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Border Width</h4>
        <div style={{ maxWidth: "200px" }}>
          <input
            type="text"
            value={borders.width || "1px"}
            onChange={(e) => onChange("width", e.target.value)}
            style={inputStyle}
            placeholder="e.g., 1px"
          />
        </div>
      </div>

      {/* Vik (Jan 23, 2026) : Shadow Presets */}
      <div>
        <h4 style={{ color: "#fff", marginBottom: "12px" }}>Shadow Presets</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
          }}
        >
          {["shadowSmall", "shadowMedium", "shadowLarge"].map((key) => (
            <div
              key={key}
              style={{
                padding: "16px",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "8px",
              }}
            >
              <label style={labelStyle}>
                {formatLabel(key.replace("shadow", ""))}
              </label>
              <input
                type="text"
                value={borders[key] || ""}
                onChange={(e) => onChange(key, e.target.value)}
                style={{ ...inputStyle, marginBottom: "12px" }}
                placeholder="e.g., 0 4px 6px rgba(0,0,0,0.1)"
              />
              {/* Vik (Jan 23, 2026) : Visual preview of shadow */}
              <div
                style={{
                  width: "100%",
                  height: "40px",
                  background: "#fff",
                  borderRadius: "8px",
                  boxShadow: borders[key] || "none",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : SHARED COMPONENTS
   Reusable input components for the editors
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Vik (Jan 23, 2026) : Color input with picker and text input
 */
function ColorInput({ label, value, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px",
        background: "rgba(255,255,255,0.05)",
        borderRadius: "8px",
      }}
    >
      {/* Vik (Jan 23, 2026) : Color picker */}
      <input
        type="color"
        value={value.startsWith("#") ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "40px",
          height: "40px",
          padding: 0,
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      />
      <div style={{ flex: 1 }}>
        <label
          style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.8rem",
            display: "block",
          }}
        >
          {label}
        </label>
        {/* Vik (Jan 23, 2026) : Text input for manual hex/rgba entry */}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            ...inputStyle,
            marginTop: "4px",
            padding: "6px 10px",
            fontSize: "0.85rem",
          }}
          placeholder="#hex or rgba(...)"
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : UTILITY FUNCTIONS
   Helper functions for data manipulation
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Vik (Jan 23, 2026) : Deep merge two objects (target + source)
 * Source values override target values
 */
function deepMerge(target, source) {
  const result = { ...target };
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

/**
 * Vik (Jan 23, 2026) : Set a nested value in an object using dot notation path
 * Example: setNestedValue(obj, "colors.primary", "#fff")
 */
function setNestedValue(obj, path, value) {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}

/**
 * Vik (Jan 23, 2026) : Format camelCase or kebab-case to Title Case
 * Example: "heading-1" -> "Heading 1", "textSecondary" -> "Text Secondary"
 */
function formatLabel(str) {
  return str
    .replace(/-/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/* ═══════════════════════════════════════════════════════════════════════════
   Vik (Jan 23, 2026) : SHARED STYLES
   Reusable style objects for consistency
   ═══════════════════════════════════════════════════════════════════════════ */

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  background: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: "6px",
  color: "#fff",
  fontSize: "0.9rem",
  outline: "none",
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

const labelStyle = {
  color: "rgba(255,255,255,0.7)",
  fontSize: "0.8rem",
  display: "block",
  marginBottom: "6px",
};
